// Programme catalogue + app-wide programme filter — ported from legacy
// loadProgrammes/programmeAccent/progDot/setProgramme/openProgrammePicker
// (v20 lines ~1384-1467). Phase 1 ships the CHROME AND STATE only: the pill,
// the accessible dropdown, persistence and the store slices. NO data
// filtering yet — Phase 2 loaders read SLICES.FILTERS.programme (and the
// applyProgrammeFilter port) to actually scope queries, and views render the
// "Showing X only" banner from the same state (programmeBannerCopy below).
//
// The storage key is the EXACT legacy key: v_20 and 2.0 run side by side
// against the same DB during the parallel phase and must agree on the
// nurse's active programme.

import { store, SLICES } from './state/store.js';
import { getClient } from './db/client.js';
import { ok, fail } from './db/client.js';
import { TABLES } from './db/contract.js';
import { esc } from './ui/esc.js';
import { toast } from './ui/toast.js';
import { register } from './ui/actions.js';

export const PROGRAMME_KEY = 'tumaini_active_programme'; // legacy key — shared with v_20

// ─── Accent safety (legacy programmeAccent + Blueprint §3 luminance guard) ──

// Neutral slate fallback — the same value legacy used for unknown/invalid
// accents (and contract.js's 'enrolled' stage colour).
export const ACCENT_FALLBACK = '#94a3b8';

/**
 * Validate a brand accent from the DB. brand_accent_hex is FREE TEXT rendered
 * into markup — a value like "red;background:url(javascript:…)" must never
 * escape. Exact legacy regex; anything else gets the neutral fallback.
 * @param {any} raw programmes.brand_accent_hex
 * @returns {string} a safe #hex colour
 */
export function accentHex(raw) {
  const hex = typeof raw === 'string' ? raw : '';
  return /^#[0-9A-Fa-f]{3,8}$/.test(hex) ? hex : ACCENT_FALLBACK;
}

/**
 * WCAG relative luminance of a #hex colour (alpha ignored), or null when the
 * hex cannot be parsed.
 * @param {string} hex #RGB / #RGBA / #RRGGBB / #RRGGBBAA
 * @returns {number|null} 0 (black) … 1 (white)
 */
export function relLuminance(hex) {
  const m = /^#([0-9A-Fa-f]{3,8})$/.exec(hex || '');
  if (!m) return null;
  let h = m[1];
  if (h.length === 3 || h.length === 4) h = [...h].map((c) => c + c).join('');
  if (h.length !== 6 && h.length !== 8) return null;
  const chan = (/** @type {number} */ i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(0) + 0.7152 * chan(2) + 0.0722 * chan(4);
}

/**
 * Blueprint §3 luminance guard: a near-white accent dot would vanish on the
 * white top bar, so it gets a --line ring (CSS class, not a colour change —
 * the brand hex still paints the dot). Unparseable values are already the
 * fallback via accentHex, but defensively ring them too.
 * @param {string} hex
 * @returns {boolean}
 */
export function accentNeedsRing(hex) {
  const l = relLuminance(hex);
  return l === null || l > 0.75;
}

/**
 * Programme dot as inline SVG — fill is an ATTRIBUTE, not a style, so it
 * works under style-src 'self' (no inline styles anywhere in 2.0). Every dot
 * carries its programme name for screen readers + tooltip (legacy progDot).
 * @param {string} name display name (for the accessible label)
 * @param {any} rawHex programmes.brand_accent_hex (validated here)
 * @param {number} [size] px
 * @returns {string}
 */
export function progDotHTML(name, rawHex, size = 8) {
  const hex = accentHex(rawHex);
  const ring = accentNeedsRing(hex) ? ' ring' : '';
  const label = esc('Programme: ' + name);
  return (
    `<svg class="prog-dot${ring}" width="${size}" height="${size}" viewBox="0 0 10 10" role="img" aria-label="${label}">` +
    `<title>${esc(name)}</title><circle cx="5" cy="5" r="4.5" fill="${esc(hex)}"/></svg>`
  );
}

// ─── Pure state helpers ────────────────────────────────────────────────────

/**
 * A stored/requested programme is only valid if it exists in the loaded
 * catalogue — anything else (stale code, removed programme, garbage) falls
 * back to 'all' (legacy handleAccess line 1188).
 * @param {any} code
 * @param {Record<string, any>} byCode catalogue keyed by code
 * @returns {string} 'all' or a real programme code
 */
export function validActive(code, byCode) {
  if (code === 'all') return 'all';
  return code && byCode && Object.prototype.hasOwnProperty.call(byCode, code) ? code : 'all';
}

/**
 * Pill label (legacy setProgramme's label branch).
 * @param {string} active 'all' or code
 * @param {Record<string, any>} byCode
 * @returns {string}
 */
export function pillLabel(active, byCode) {
  if (active === 'all') return 'All programmes';
  const p = byCode && byCode[active];
  return (p && p.display_name) || active;
}

/**
 * Filter toast copy — exact legacy wording (v20 line 1466).
 * @param {string} active
 * @param {Record<string, any>} byCode
 * @returns {string}
 */
export function programmeToast(active, byCode) {
  return 'Filter: ' + (active === 'all' ? 'All programmes' : pillLabel(active, byCode));
}

/**
 * Legacy renderProgrammeBanner copy (v20 line 1380), pure, for Phase 2 views.
 * The per-view noun matters: the intake list shows consents and the safety
 * register shows adverse events — "patients … are hidden" would be wrong
 * (and, on safety, dangerously vague).
 * @param {string} name programme display name
 * @param {string} [noun] 'patients' (default) | 'consents' | 'adverse events'
 * @returns {string} HTML (name is esc()-ed here)
 */
export function programmeBannerCopy(name, noun = 'patients') {
  return `Showing <b>${esc(name)}</b> only — ${noun} in other programmes are hidden.`;
}

/**
 * Read the persisted programme choice. Never throws.
 * @param {any} [storage]
 * @returns {string|null}
 */
export function readActiveProgramme(storage) {
  try {
    const st = storage || /** @type {any} */ (globalThis).localStorage;
    return st.getItem(PROGRAMME_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Persist the programme choice under the shared legacy key. Never throws.
 * @param {string} code
 * @param {any} [storage]
 * @returns {void}
 */
export function persistActiveProgramme(code, storage) {
  try {
    const st = storage || /** @type {any} */ (globalThis).localStorage;
    st.setItem(PROGRAMME_KEY, code);
  } catch {
    /* storage unavailable — in-memory filter still applies this session */
  }
}

// ─── Catalogue load (legacy loadProgrammes) ────────────────────────────────

/**
 * Load active programmes (is_active, sort_order — exact legacy query) into
 * the PROGRAMMES slice and settle the persisted filter into FILTERS.
 * A failed load leaves the pill hidden and the filter on 'all' — the legacy
 * behaviour (console.warn, no catalogue, switcher never shown). Never throws.
 * @param {{client?: any, storage?: any}} [deps] test injection
 * @returns {Promise<{ok: boolean}>} envelope (data in the store, not returned)
 */
export async function loadProgrammes(deps) {
  const client = (deps && deps.client) || getClient();
  /** @type {any} */
  let res;
  try {
    res = await client
      .from(TABLES.PROGRAMMES)
      .select('*')
      .eq('is_active', true)
      .order('sort_order');
  } catch (e) {
    res = { data: null, error: e };
  }
  if (res.error) {
    // Parity: legacy swallowed this (console.warn) — the app runs unfiltered.
    console.warn('Could not load programmes', res.error);
    return fail(res.error);
  }
  const list = res.data || [];
  /** @type {Record<string, any>} */
  const byCode = {};
  for (const p of list) byCode[p.code] = p;
  const active = validActive(readActiveProgramme(deps && deps.storage), byCode);
  store.set(SLICES.PROGRAMMES, { list, byCode, active });
  store.set(SLICES.FILTERS, { programme: active });
  return ok(list);
}

// ─── Switch (legacy setProgramme) ──────────────────────────────────────────

/**
 * Set the app-wide programme filter: validate, persist (legacy key), publish
 * to both slices, toast. Phase 2 adds the legacy re-nav of the current view;
 * for now subscribed chrome re-renders and loaders will pick the filter up.
 * @param {string} code 'all' or a programme code
 * @param {{silent?: boolean, storage?: any}} [opts] silent: no toast (initial login)
 * @returns {void}
 */
export function setProgramme(code, opts) {
  const cur = store.get(SLICES.PROGRAMMES) || {};
  const active = validActive(code, cur.byCode || {});
  persistActiveProgramme(active, opts && opts.storage);
  store.set(SLICES.PROGRAMMES, { active });
  store.set(SLICES.FILTERS, { programme: active });
  if (opts && opts.silent) return;
  toast(programmeToast(active, cur.byCode || {}), 'ok');
}

// ─── Pill + accessible dropdown (chrome) ───────────────────────────────────

const CHEVRON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="prog-chev"><polyline points="6 9 12 15 18 9"/></svg>';

function menuEl() {
  const doc = /** @type {any} */ (globalThis).document;
  return doc && doc.getElementById('prog-menu');
}

function pillEl() {
  const doc = /** @type {any} */ (globalThis).document;
  return doc && doc.getElementById('prog-pill');
}

/** @returns {boolean} */
function menuOpen() {
  const m = menuEl();
  return !!m && !m.hidden;
}

/**
 * Close the programme dropdown (idempotent).
 * @param {boolean} [refocus] return focus to the pill (Escape semantics)
 * @returns {void}
 */
export function closeProgrammeMenu(refocus) {
  const m = menuEl();
  const p = pillEl();
  if (m) m.hidden = true;
  if (p) {
    p.setAttribute('aria-expanded', 'false');
    if (refocus) p.focus();
  }
}

function openProgrammeMenu() {
  const m = menuEl();
  const p = pillEl();
  if (!m || !p) return;
  renderMenu();
  m.hidden = false;
  p.setAttribute('aria-expanded', 'true');
  const checked = m.querySelector('[aria-checked="true"]') || m.querySelector('[role="menuitemradio"]');
  if (checked) /** @type {any} */ (checked).focus();
}

function renderPill() {
  const p = pillEl();
  if (!p) return;
  const prog = store.get(SLICES.PROGRAMMES);
  if (!prog || !prog.list || !prog.list.length) {
    // Legacy parity: the switcher only appears once the catalogue has loaded.
    p.hidden = true;
    return;
  }
  p.hidden = false;
  const active = prog.active || 'all';
  const dot =
    active === 'all'
      ? // The all-programmes dot is brand navy via CSS (no per-programme hex).
        '<svg class="prog-dot all" width="8" height="8" viewBox="0 0 10 10" aria-hidden="true"><circle cx="5" cy="5" r="4.5"/></svg>'
      : progDotHTML(pillLabel(active, prog.byCode), prog.byCode[active] && prog.byCode[active].brand_accent_hex);
  p.innerHTML = dot + `<span class="prog-lbl">${esc(pillLabel(active, prog.byCode))}</span>` + CHEVRON;
}

function renderMenu() {
  const m = menuEl();
  if (!m) return;
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const active = prog.active || 'all';
  const items = [
    `<button role="menuitemradio" aria-checked="${active === 'all'}" class="prog-opt" data-action="prog-set" data-code="all">` +
      '<svg class="prog-dot all" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><circle cx="5" cy="5" r="4.5"></circle></svg>' +
      '<span class="prog-opt-txt"><span class="prog-opt-name">All programmes</span>' +
      '<span class="prog-opt-sub">See work across every product</span></span></button>',
    ...(prog.list || []).map((/** @type {any} */ p) => {
      const name = p.display_name || p.code;
      const sub = [p.default_drug_name, p.indication].filter(Boolean).join(' · ');
      return (
        `<button role="menuitemradio" aria-checked="${active === p.code}" class="prog-opt" data-action="prog-set" data-code="${esc(p.code)}">` +
        progDotHTML(name, p.brand_accent_hex, 10) +
        `<span class="prog-opt-txt"><span class="prog-opt-name">${esc(name)}</span>` +
        (sub ? `<span class="prog-opt-sub">${esc(sub)}</span>` : '') +
        '</span></button>'
      );
    }),
  ];
  m.innerHTML = items.join('');
}

let _wired = false;

/**
 * Mount the programme pill into the shell's #prog-slot, wire actions and
 * subscriptions, and load the catalogue. Call once after mountShell().
 * @returns {Promise<void>}
 */
export async function initProgrammes() {
  const doc = /** @type {any} */ (globalThis).document;
  const slot = doc && doc.getElementById('prog-slot');
  if (!slot) return;
  slot.innerHTML =
    '<span class="prog-wrap">' +
    '<button class="prog-pill" id="prog-pill" data-action="prog-toggle" ' +
    'aria-haspopup="menu" aria-expanded="false" title="Switch programme" hidden></button>' +
    '<div class="prog-menu" id="prog-menu" role="menu" aria-label="Programme filter" hidden></div>' +
    '</span>';

  if (!_wired) {
    _wired = true;
    register('prog-toggle', () => {
      if (menuOpen()) closeProgrammeMenu();
      else openProgrammeMenu();
    });
    register('prog-set', (el) => {
      closeProgrammeMenu(true);
      setProgramme(el.getAttribute('data-code') || 'all');
    });
    // Escape closes and refocuses the pill; outside interactions just close.
    // CAPTURE phase: main.js's Escape→closeModal listener is a bubble-phase
    // document listener registered earlier — only a capture-phase
    // stopPropagation runs before it, so Escape with the menu open closes
    // the MENU only and never also slams a modal underneath.
    doc.addEventListener('keydown', (/** @type {any} */ e) => {
      if (!menuOpen()) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeProgrammeMenu(true);
      } else if (e.key === 'Tab') {
        // ARIA menu pattern: Tab closes the menu. Refocus the pill FIRST so
        // the browser's default Tab proceeds naturally from it (next control
        // after the pill / previous on Shift+Tab) instead of from a
        // just-hidden menu item — and aria-expanded never lies while focus
        // sits behind an open overlay.
        closeProgrammeMenu(true);
      } else if (
        e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End'
      ) {
        const m = menuEl();
        const items = m ? [...m.querySelectorAll('[role="menuitemradio"]')] : [];
        if (!items.length) return;
        e.preventDefault();
        /** @type {any} */
        let next;
        if (e.key === 'Home') next = items[0];
        else if (e.key === 'End') next = items[items.length - 1];
        else {
          const i = items.indexOf(doc.activeElement);
          next =
            e.key === 'ArrowDown'
              ? items[(i + 1) % items.length]
              : items[(i - 1 + items.length) % items.length];
        }
        /** @type {any} */ (next).focus();
      }
    }, true); // capture — see comment above
    doc.addEventListener('click', (/** @type {any} */ e) => {
      if (!menuOpen()) return;
      const t = e.target;
      if (t && typeof t.closest === 'function' && t.closest('.prog-wrap')) return;
      closeProgrammeMenu();
    });
    store.subscribe(SLICES.PROGRAMMES, () => {
      renderPill();
      if (menuOpen()) renderMenu();
    });
  }
  renderPill();
  await loadProgrammes();
}
